import useCustomHook from '@/hooks/useCustomHook';
import { minutesSecondsToSeconds } from '@/modules/activities/playerTimeAndProgress';
import { getAnalyticsActivityProps } from './getAnalyticsProps';
import { usePlausibleContext } from './PlausibleAnalytics';

export default function useActivityAnalytics({ activity, activityConf = {} }) {
  const plausibleContext = usePlausibleContext();
  const sendPlausibleEvent = plausibleContext.actions.sendPlausibleEvent;

  const { state, actions } = useCustomHook({
    defaultState: {
      onEndSent: false,
    },
    actions: {
      onStart: (data) => {
        try {
          const eventName = data?.eventName ?? 'activity_started';
          // Resetting onEndSent here because, if not on a task, when you replay one video or song, there is not a restart event
          actions.setState({ onEndSent: false });
          sendPlausibleEvent(
            eventName,
            getAnalyticsActivityProps(activity, activityConf)
          );
        } catch (err) {
          console.error(err);
          sendAnalyticsErrorEvent(
            {
              eventName: eventName ?? 'activity_finished',
              handler: 'onStart',
            },
            err
          );
        }
      },
      onProgress: (
        data = {
          enventName: null,
          progressData: { progress: 0 },
        }
      ) => {
        try {
          const {
            eventName,
            progressData: { progress },
          } = data;
          if (!state.onEndSent) {
            if (progress >= 100) {
              // Resetting onStartSent here because, if not on a task, when you replay one video or song, there is not a restart event
              actions.setState({ onEndSent: true });

              sendPlausibleEvent(
                eventName ?? 'activity_finished',
                getAnalyticsActivityProps(activity, activityConf)
              );
            }
          }
        } catch (err) {
          sendAnalyticsErrorEvent(
            {
              eventName: eventName ?? 'activity_finished',
              handler: 'onProgress',
            },
            err
          );
        }
      },
      onMediaProgress: (
        data = {
          enventName: null,
          progressData: { playedSeconds: 0, duration: 0 },
        }
      ) => {
        try {
          const { eventName, progressData } = data;
          if (!state.onEndSent) {
            const { playedSeconds, duration } = progressData;
            if (duration) {
              const taskDuration = activity.data.taskDuration
                ? minutesSecondsToSeconds(activity.data.taskDuration)
                : duration;
              if (playedSeconds >= taskDuration) {
                // Resetting onStartSent here because if not on a task when you replay one video or song there is not onRestart event
                actions.setState({ onEndSent: true });
                sendPlausibleEvent(
                  eventName ?? 'activity_finished',
                  getAnalyticsActivityProps(activity, activityConf)
                );
              }
            }
          }
        } catch (err) {
          sendAnalyticsErrorEvent(
            {
              eventName: eventName ?? 'activity_finished',
              handler: 'onMediaProgress',
            },
            err
          );
        }
      },
      onFinish: (data = { enventName: undefined }) => {
        try {
          const { eventName } = data;
          if (!state.onEndSent) {
            actions.setState({ onEndSent: true });
            sendPlausibleEvent(
              eventName ?? 'activity_finished',
              getAnalyticsActivityProps(activity, activityConf)
            );
          }
        } catch (err) {
          sendAnalyticsErrorEvent(
            {
              eventName: eventName ?? 'activity_finished',
              hanlder: 'onFinish',
            },
            err
          );
        }
      },
      reset: () => {
        console.log('[RESET]');
        actions.setState({ onEndSent: false });
      },
    },
  });

  return { actions };
}
